import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { Loader, FlexGrid } from '@gasbuddy/react-components';
import MainLayout from '../../components/main-layout';
import StationSelector from '../../components/station-selector';
import Tabs from '../../components/tabs';
import Notification from '../../components/notification';
import StationProfileTab from '../../components/station-profile-tab';
import ReputationTab from '../../components/reputation-tab';
import InsightsTabContent from '../../components/insights-tab';
import CompetitionTabContent from '../../components/competition-tab';
import CTAContainer from '../../components/cta-container';
import StationBanner from './station-banner';
import { Solutions } from '../../../lib/constants/enums';
import StationPropTypes from '../../utils/prop-types/station';
import { setBrands } from '../../actions';
import BuildReportButton from '../../components/build-report-button';
import SharePageButton from '../../components/share-page-button';
import styles from './styles.css';
import NewBadge from '../../components/new-badge';
import BPUtils from '../../../lib/bputils';
import PermissionsUtils from '../../../lib/permissions';

const tabs = [
  { label: 'Profile', content: (<StationProfileTab />), gTagTitle: 'Profile' },
  { label: 'Reputation', content: (<ReputationTab />), gTagTitle: 'Reputation' },
  {
    label: 'Station Insights',
    content: (<InsightsTabContent />),
    gTagTitle: 'Station Insights',
  },
  {
    label: (
      <>
        Competition
        <NewBadge expiryDate="2021-11-31" />
      </>
    ),
    content: (<CompetitionTabContent />),
    gTagTitle: 'Competition',
  },
];

const noAccess = !BPUtils.isFeatureEnabled('emergency_features') && PermissionsUtils.isFuelShortageUser();

function StationProfile({ station, dispatchSetBrands, isLoading: isLoadingProps }) {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    getBrands();
  }, []);

  const getBrands = async () => {
    const res = await BPUtils.api.get({
      path: '/brands',
      requestId: 'stationInformationGetAllBrands',
    });

    dispatchSetBrands(res);
  };

  const onStationSelectorReady = () => {
    setIsLoading(false);
  };

  React.useEffect(() => {
    setIsLoading(isLoadingProps);
  }, [isLoadingProps]);

  const handleGenerateReport = (email, comments, startDate, endDate, comparisonMetric) => {
    const { id: stationId } = station;
    const data = {
      station_id: stationId,
      start_date: startDate,
      end_date: endDate,
      comparison_type: comparisonMetric.metricApiName,
      comparison_target_id: comparisonMetric.target ? comparisonMetric.target : 0,
    };

    BPUtils.api.post({
      path: '/reports/station-profile',
      data: { ...data, recipients: email, message: comments },
    })
      .then(() => { Notification.success('Report requested!'); })
      .catch(() => Notification.error('There was an error requesting report. Please contact support.'));
  };

  const getTabs = () => {
    const newTabs = [];

    newTabs.push(tabs[0]);

    if (station && !PermissionsUtils.isFuelShortageUser()
      && BPUtils.hasSolution(station.solutions.map(s => s.name), Solutions.ReputationManagement)) {
      newTabs.push(tabs[1]);
    }

    // station insights is going to show for everyone, not just insight solution (except Fuel Shortage User)
    if (!PermissionsUtils.isFuelShortageUser()) {
      newTabs.push(tabs[2]);
    }

    newTabs.push(tabs[3]);

    return newTabs;
  };

  return (
    <MainLayout title="Station Profile" noAccess={noAccess} loading={isLoading}>
      {
          isLoading && <Loader className={styles.center} />
        }
      {station && (
      <>
        <CTAContainer alignment="right">
          <FlexGrid.Column>
            <BuildReportButton
              onGenerateReport={handleGenerateReport}
              gTagTitle="Station Profile - Build Report"
            />
            <SharePageButton gTagTitle="Station Profile - Share Report" />
          </FlexGrid.Column>
        </CTAContainer>
      </>
      )}
      <div className={styles.stationSelectorContainer} style={{ display: `${isLoading ? 'none' : 'block'}` }}>
        <StationSelector onReady={onStationSelectorReady} />
      </div>
      {station
          && (
            <>
              <div style={{ paddingTop: '25px' }}>
                <StationBanner />
                <Tabs
                  tabs={getTabs(station)}
                  gTagTitle="Station Profile Tabs"
                />
              </div>
            </>
          )}
    </MainLayout>
  );
}

StationProfile.propTypes = {
  station: StationPropTypes,
  dispatchSetBrands: PropTypes.func,
  isLoading: PropTypes.bool,
};

StationProfile.defaultProps = {
  station: null,
  dispatchSetBrands: null,
  isLoading: true,
};

const mapStateToProps = state => ({
  station: state.station,
  isLoading: state.loading,
});

const mapDispatchToProps = dispatch => ({
  dispatchSetBrands: brands => dispatch(setBrands(brands)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StationProfile);
